import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { X, Cpu, Lock, Crown, Volume2, VolumeX, Film, Play, RefreshCw, Star, ShieldCheck, Info, LogOut, Mic, Columns2, RectangleHorizontal, AudioLines, Bell } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { playBell } from "@/lib/bell";
import { useAuth } from "@/context/AuthContext";
import MovieStarConsentModal from "@/components/MovieStarConsentModal";
import MovieStarHowItWorks from "@/components/MovieStarHowItWorks";
import ContactModal from "@/components/ContactModal";
import TopUpModal from "@/components/TopUpModal";
import { Wallet, Video, Zap } from "lucide-react";

const CREDITS_PER_VIDEO = 22; // 1 package = a full manual + its narrated video

export default function SettingsModal({ open, onClose }) {
  const { user, refresh } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [soundOn, setSoundOn] = useState(() => (typeof localStorage !== "undefined" ? localStorage.getItem("sms_sound") !== "off" : true));
  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    try { localStorage.setItem("sms_sound", next ? "on" : "off"); } catch { /* ignore */ }
    if (next) playBell();  // quick preview so they hear the ding when turning it on
  };
  const [showAdmin, setShowAdmin] = useState(false);
  const [adminCode, setAdminCode] = useState("");
  const [adminBusy, setAdminBusy] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const previewRef = useRef(null);

  // MovieStar Mode
  const [msHowOpen, setMsHowOpen] = useState(false);
  const [msConsentOpen, setMsConsentOpen] = useState(false);
  const [msBusy, setMsBusy] = useState(false);
  const [msBuyBusy, setMsBuyBusy] = useState(false);
  const [msBetaOpen, setMsBetaOpen] = useState(false);
  const [msBetaCode, setMsBetaCode] = useState("");
  const [msAvailable, setMsAvailable] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [imgProvider, setImgProvider] = useState("gemini");
  const [imgBusy, setImgBusy] = useState(false);
  const [affTag, setAffTag] = useState("");
  const [affBusy, setAffBusy] = useState(false);
  const [affCode, setAffCode] = useState("");
  const [affCodeSent, setAffCodeSent] = useState(false);
  const [affSentTo, setAffSentTo] = useState("");
  const [affCodeBusy, setAffCodeBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setAffTag(user?.amazon_affiliate_tag || "");
      setAffCode(""); setAffCodeSent(false); setAffSentTo("");
    }
  }, [open, user?.amazon_affiliate_tag]);

  const savedTag = user?.amazon_affiliate_tag || "";
  const tagChanged = affTag.trim() !== savedTag;
  const clearingTag = affTag.trim() === "" && savedTag !== "";

  const requestAffCode = async () => {
    setAffCodeBusy(true);
    try {
      const { data } = await api.post("/me/affiliate-tag/request-code", {});
      setAffCodeSent(true);
      setAffSentTo(data.sent_to || "your email");
      toast.success(`Code sent to ${data.sent_to || "your email"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send a code");
    } finally {
      setAffCodeBusy(false);
    }
  };

  const saveAffTag = async () => {
    setAffBusy(true);
    try {
      const { data } = await api.post("/me/affiliate-tag", { tag: affTag.trim(), code: affCode.trim() || undefined });
      setAffTag(data.amazon_affiliate_tag || "");
      setAffCode(""); setAffCodeSent(false); setAffSentTo("");
      await refresh();
      toast.success(data.amazon_affiliate_tag ? "Amazon Associate ID saved ✅" : "Amazon Associate ID cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your Amazon ID");
    } finally {
      setAffBusy(false);
    }
  };

  useEffect(() => {
    if (!open || !user?.unlimited) return;
    api.get("/config").then((r) => setImgProvider(r.data?.image_provider || "gemini")).catch(() => {});
  }, [open, user?.unlimited]);

  const setProvider = async (provider) => {
    if (provider === imgProvider) return;
    setImgBusy(true);
    try {
      await api.post("/admin/image-provider", { provider });
      setImgProvider(provider);
      toast.success(provider === "openai" ? "Image engine: ChatGPT (GPT Image 1)" : "Image engine: Gemini");
    } catch {
      toast.error("Couldn't switch image engine");
    } finally {
      setImgBusy(false);
    }
  };

  useEffect(() => () => { if (previewRef.current) previewRef.current.pause(); }, []);
  useEffect(() => { if (!open && previewRef.current) { previewRef.current.pause(); setPreviewing(null); } }, [open]);

  const doUnlock = async () => {
    setAdminBusy(true);
    try {
      await api.post("/admin/unlock", { passcode: adminCode });
      await refresh();
      toast.success("Admin unlocked");
      setShowAdmin(false);
      setAdminCode("");
    } catch {
      toast.error("Incorrect passcode");
    } finally {
      setAdminBusy(false);
    }
  };

  const doLock = async () => {
    setAdminBusy(true);
    try {
      await api.post("/admin/lock");
      await refresh();
      toast.success("Logged out of Admin");
    } catch {
      toast.error("Couldn't log out of Admin");
    } finally {
      setAdminBusy(false);
    }
  };

  const changePassword = async () => {
    if (pwNew.length < 8) return toast.error("New password must be at least 8 characters");
    if (pwNew !== pwConfirm) return toast.error("New passwords don't match");
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwCurrent, new_password: pwNew });
      toast.success("Password updated");
      setPwOpen(false); setPwCurrent(""); setPwNew(""); setPwConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't change password");
    } finally {
      setPwBusy(false);
    }
  };

  useEffect(() => {
    if (open) {
      api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
      api.get("/config").then(({ data }) => setMsAvailable(data.moviestar_available !== false)).catch(() => {});
    }
  }, [open]);

  if (!open) return null;

  const save = async (next) => {
    setSettings(next);
    setSaving(true);
    try {
      await api.put("/settings", {
        step_illustrations: next.step_illustrations,
        voice_over: next.voice_over,
        music_gain: next.music_gain,
        voice_gain: next.voice_gain,
        intro_gain: next.intro_gain,
        outro_gain: next.outro_gain,
        music_muted: next.music_muted,
        voice_muted: next.voice_muted,
        auto_duck: next.auto_duck,
        duck_level: next.duck_level,
        music_track: next.music_track,
        music_variant: next.music_variant,
        moviestar_step_photos: next.moviestar_step_photos,
        studio_labels: next.studio_labels,
        caption_style: next.caption_style,
      });
      await refresh();
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const toggleIllustrations = () =>
    save({ ...settings, step_illustrations: !settings.step_illustrations });

  const toggleVoiceOver = () =>
    save({ ...settings, voice_over: !settings.voice_over });

  const toggleStudioLabels = () =>
    save({ ...settings, studio_labels: settings.studio_labels === false ? true : false });

  const setCaptionStyle = (style) => {
    if ((settings.caption_style || "columns") === style) return;
    save({ ...settings, caption_style: style });
  };

  const setGain = (key, delta) => {
    const defs = { music_gain: 3, intro_gain: 6, outro_gain: 6 };
    const cur = settings[key] ?? (defs[key] ?? 8);
    const nextVal = Math.max(1, Math.min(10, cur + delta));
    save({ ...settings, [key]: nextVal });
  };
  const toggleMute = (key) => save({ ...settings, [key]: !settings[key] });
  const toggleAutoDuck = () => save({ ...settings, auto_duck: settings.auto_duck === false ? true : false });
  const setDuckLevel = (lvl) => { if ((settings.duck_level || "medium") === lvl) return; save({ ...settings, duck_level: lvl }); };

  const playPreview = (id, variant) => {
    try {
      if (previewRef.current) { previewRef.current.pause(); }
      const a = new Audio(`${process.env.REACT_APP_BACKEND_URL}/api/music/preview/${id}?variant=${variant}`);
      previewRef.current = a;
      setPreviewing(id);
      a.onended = () => setPreviewing(null);
      a.play().catch(() => setPreviewing(null));
    } catch {
      setPreviewing(null);
    }
  };

  const chooseTrack = (id) => {
    save({ ...settings, music_track: id, music_variant: 0 });
    playPreview(id, 0);
  };

  const refreshTrack = () => {
    const id = settings.music_track || "island";
    const next = ((settings.music_variant || 0) + 1) % 8;
    save({ ...settings, music_track: id, music_variant: next });
    playPreview(id, next);
  };

  const saveMoviestar = async (enabled) => {
    const next = { ...settings, moviestar_enabled: enabled };
    setSettings(next);
    try {
      await api.put("/settings", { moviestar_enabled: enabled });
      await refresh();
    } catch {
      toast.error("Could not save");
    }
  };

  const onMoviestarToggle = () => {
    if (settings.moviestar_enabled) {
      saveMoviestar(false); // turning off needs no consent
    } else {
      setMsConsentOpen(true); // turning on → must agree every time
    }
  };

  const agreeMoviestar = async () => {
    setMsBusy(true);
    try {
      await api.post("/moviestar/consent", {});
      await saveMoviestar(true);
      setMsConsentOpen(false);
      toast.success("MovieStar Mode on — add your photo when you create a manual");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not enable MovieStar Mode");
    } finally {
      setMsBusy(false);
    }
  };

  const buyMoviestar = async () => {
    setMsBuyBusy(true);
    try {
      const { data } = await api.post("/moviestar/checkout", { package_id: "moviestar", origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setMsBuyBusy(false);
    }
  };

  const betaUnlock = async () => {
    setMsBusy(true);
    try {
      await api.post("/moviestar/beta-unlock", { passcode: msBetaCode });
      await refresh();
      toast.success("MovieStar Mode unlocked for 30 days 🎬");
      setMsBetaOpen(false);
      setMsBetaCode("");
    } catch {
      toast.error("Incorrect passcode");
    } finally {
      setMsBusy(false);
    }
  };

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[#171426]/50 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
      data-testid="settings-modal-overlay"
    >
      <div
        className="settings-dark bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto sms-fade-up"
        style={{ border: "2px solid #5d8a52", boxShadow: "0 30px 80px -24px rgba(0,0,0,.75), 0 0 44px -10px rgba(93,138,82,.4)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="settings-modal"
      >
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 pt-6 pb-4 border-b border-[#f0eef7]">
          <h2 className="text-2xl font-extrabold">Settings</h2>
          <button data-testid="settings-close-btn" onClick={onClose} className="p-1.5 rounded-full hover:bg-[#eef3ea] text-[#6b6880]">
            <X size={22} />
          </button>
        </div>

        {!settings ? (
          <div className="p-10 text-center text-[#6b6880]">Loading…</div>
        ) : (
          <div className="px-6 py-5 space-y-8">
            {/* Credit meter — depletes as you create; top up anytime */}
            <div data-testid="credit-meter" className="rounded-2xl border-2 p-4" style={{ borderColor: "#5d8a52", background: "#14261a" }}>
              {user?.unlimited ? (
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-xs font-bold text-[#9a96ac] uppercase tracking-wide">Your balance</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Crown size={22} className="text-[#5d8a52]" />
                      <span data-testid="credit-meter-unlimited" className="text-2xl font-extrabold text-[#171426]">Unlimited</span>
                    </div>
                    <p className="text-xs text-[#9a96ac] mt-1">Admin account — create as much as you like.</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="text-xs font-bold text-[#9a96ac] uppercase tracking-wide">Cash balance</div>
                      <div className="flex items-baseline gap-1.5 mt-0.5">
                        <Wallet size={20} className="text-[#5d8a52] self-center" />
                        <span data-testid="credit-meter-balance" className="text-3xl font-extrabold text-[#171426]">${(((user?.credits ?? 0)) / 100).toFixed(2)}</span>
                      </div>
                      <div data-testid="credit-meter-videos" className="flex items-center gap-1.5 text-sm font-semibold text-[#4f7746] mt-1">
                        <Video size={14} /> ≈ {Math.floor((user?.credits ?? 0) / 399)} video{Math.floor((user?.credits ?? 0) / 399) === 1 ? "" : "s"} left
                      </div>
                    </div>
                    <button
                      data-testid="credit-meter-topup-btn"
                      onClick={() => setTopUpOpen(true)}
                      className="shrink-0 flex items-center gap-1.5 font-bold text-white px-4 py-2.5 rounded-xl transition-colors"
                      style={{ background: "#4f7746" }}
                    >
                      <Wallet size={16} /> Add funds
                    </button>
                  </div>
                  {(user?.credits ?? 0) < 399 && (
                    <p data-testid="credit-meter-low" className="text-xs text-[#b45309] font-semibold mt-3 bg-[#fdf7e3] border border-[#f7e6a8] rounded-lg px-3 py-2">
                      Running low — add funds to keep creating videos.
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Creator perk — monetize with your own Amazon Associate ID */}
            {(
              <div data-testid="creator-affiliate" className="rounded-2xl border-2 p-4" style={{ borderColor: "#5d8a52", background: "#14141b" }}>
                <div className="flex items-center gap-2 font-bold text-lg">
                  <Crown size={18} className="text-[#5d8a52]" /> Your Amazon Associate ID
                </div>
                <p className="text-sm text-black mt-1 leading-relaxed">
                  <span className="font-extrabold text-[#4f7746]">This is your payday.</span> Add your verified Amazon Associate ID and every product link on the <span className="font-semibold text-[#171426]">manuals you publish</span> earns commission straight to <span className="font-semibold text-[#171426]">you</span> — while your viewers learn. Leave it blank to use the house default.
                </p>
                <input
                  data-testid="affiliate-tag-input"
                  value={affTag}
                  onChange={(e) => { setAffTag(e.target.value); setAffCodeSent(false); setAffCode(""); }}
                  placeholder="Enter Your Amazon Affiliate ID Here"
                  className="w-full mt-3 px-4 py-2.5 rounded-xl border-2 border-[#e0dcef] focus:border-[#5d8a52] outline-none text-sm font-semibold"
                />

                {clearingTag && (
                  <button
                    data-testid="affiliate-tag-clear"
                    onClick={saveAffTag}
                    disabled={affBusy}
                    className="mt-2 w-full px-5 py-2.5 rounded-xl font-bold text-[#4a475c] bg-[#eef1ea] hover:bg-[#e2e8dd] transition-colors disabled:opacity-60"
                  >
                    {affBusy ? "…" : "Clear my Amazon ID (use house default)"}
                  </button>
                )}

                {tagChanged && affTag.trim() !== "" && (
                  <div className="mt-3 rounded-xl border-2 border-[#5d8a52] bg-white p-3" data-testid="affiliate-verify-box">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-[#4a475c]">
                      <Lock size={13} className="text-[#5d8a52]" /> Verify it's you before saving
                    </div>
                    {!affCodeSent ? (
                      <>
                        <p className="text-[11px] text-black mt-1 leading-snug">
                          We'll email a 6-digit code to your account address. Enter it to save this Amazon ID.
                        </p>
                        <button
                          data-testid="affiliate-send-code"
                          onClick={requestAffCode}
                          disabled={affCodeBusy}
                          className="mt-2 w-full px-5 py-2.5 rounded-xl font-bold text-white bg-[#4f7746] hover:bg-[#3f6238] transition-colors disabled:opacity-60"
                        >
                          {affCodeBusy ? "Sending…" : "Email me a code"}
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="text-[11px] text-black mt-1 leading-snug">
                          Code sent to <span className="font-semibold text-[#4a475c]">{affSentTo}</span> (expires in 10 min).
                        </p>
                        <div className="flex gap-2 mt-2">
                          <input
                            data-testid="affiliate-code-input"
                            value={affCode}
                            onChange={(e) => setAffCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            inputMode="numeric"
                            placeholder="123456"
                            className="flex-1 px-4 py-2.5 rounded-xl border-2 border-[#e0dcef] focus:border-[#5d8a52] outline-none text-sm font-bold tracking-[0.4em] text-center"
                          />
                          <button
                            data-testid="affiliate-tag-save"
                            onClick={saveAffTag}
                            disabled={affBusy || affCode.trim().length !== 6}
                            className="px-5 py-2.5 rounded-xl font-bold text-white bg-[#16a34a] hover:bg-[#15803d] transition-colors disabled:opacity-60"
                          >
                            {affBusy ? "…" : "Verify & Save"}
                          </button>
                        </div>
                        <button
                          data-testid="affiliate-resend-code"
                          onClick={requestAffCode}
                          disabled={affCodeBusy}
                          className="mt-2 text-[11px] font-semibold text-[#5d8a52] hover:underline disabled:opacity-60"
                        >
                          {affCodeBusy ? "Sending…" : "Resend code"}
                        </button>
                      </>
                    )}
                  </div>
                )}
                <p className="text-[11px] text-black mt-1.5 leading-snug text-center">
                  Must be your own approved tag (it ends in <code>-20</code>).
                  <br />
                  Don't have one?{" "}
                  <a href="https://affiliate-program.amazon.com/" target="_blank" rel="noopener noreferrer" className="text-[#5d8a52] font-semibold hover:underline">Click here to join Amazon Associates</a>.
                </p>
              </div>
            )}

            {/* Step illustrations */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-bold text-lg">Step illustrations</div>
                <p className="text-sm text-[#6b6880] mt-1 leading-relaxed">
                  Auto-generate a small thumbnail for each step. Turn off to show a "Show image" button instead, for a snappier experience.
                </p>
              </div>
              <button
                data-testid="toggle-illustrations"
                onClick={toggleIllustrations}
                className="relative w-14 h-8 rounded-full transition-colors shrink-0 mt-1"
                style={{ background: settings.step_illustrations ? "#5d8a52" : "#3a3a44" }}
              >
                <span
                  className="absolute top-1 w-6 h-6 bg-white rounded-full transition-all"
                  style={{ left: settings.step_illustrations ? 28 : 4 }}
                />
              </button>
            </div>

            {/* Voice-over (accessibility) */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-bold text-lg flex items-center gap-2"><Volume2 size={18} className="text-[#5d8a52]" /> Read steps aloud</div>
                <p className="text-sm text-[#6b6880] mt-1 leading-relaxed">
                  Accessibility: a high-quality voice reads each step out loud when the step opens. Turn this off anytime to mute.
                </p>
              </div>
              <button
                data-testid="toggle-voiceover"
                role="switch"
                aria-checked={!!settings.voice_over}
                onClick={toggleVoiceOver}
                className="relative w-14 h-8 rounded-full transition-colors shrink-0 mt-1"
                style={{ background: settings.voice_over ? "#5d8a52" : "#3a3a44" }}
              >
                <span
                  className="absolute top-1 w-6 h-6 bg-white rounded-full transition-all"
                  style={{ left: settings.voice_over ? 28 : 4 }}
                />
              </button>
            </div>

            {/* Completion sound (the finished-video "ding") */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-bold text-lg flex items-center gap-2"><Bell size={18} className="text-[#5d8a52]" /> Completion sound</div>
                <p className="text-sm text-[#6b6880] mt-1 leading-relaxed">
                  Play a little bell “ding” when your video finishes rendering. Turn it off to keep things silent.
                </p>
              </div>
              <button
                data-testid="toggle-completion-sound"
                role="switch"
                aria-checked={soundOn}
                onClick={toggleSound}
                className="relative w-14 h-8 rounded-full transition-colors shrink-0 mt-1"
                style={{ background: soundOn ? "#5d8a52" : "#3a3a44" }}
              >
                <span className="absolute top-1 w-6 h-6 bg-white rounded-full transition-all" style={{ left: soundOn ? 28 : 4 }} />
              </button>
            </div>

            {/* Voice Studio */}
            <div data-testid="voice-studio-section">
              <div className="flex items-center gap-2 font-bold text-lg">
                <Mic size={18} className="text-[#5d8a52]" /> Voice Studio
              </div>
              <div className="flex items-start justify-between gap-4 border border-[#ece9f5] rounded-2xl px-4 py-3 mt-3">
                <div>
                  <div className="font-bold text-[#171426]">Show studio labels</div>
                  <p className="text-sm text-[#6b6880] mt-0.5 leading-relaxed">
                    Display text labels under the studio buttons (Back Step, Play, Record, Redo, Next Step). Turn off for a cleaner deck once you know your way around.
                  </p>
                </div>
                <button
                  data-testid="toggle-studio-labels"
                  role="switch"
                  aria-checked={settings.studio_labels !== false}
                  onClick={toggleStudioLabels}
                  className="relative w-14 h-8 rounded-full transition-colors shrink-0 mt-1"
                  style={{ background: settings.studio_labels !== false ? "#5d8a52" : "#3a3a44" }}
                >
                  <span
                    className="absolute top-1 w-6 h-6 bg-white rounded-full transition-all"
                    style={{ left: settings.studio_labels !== false ? 28 : 4 }}
                  />
                </button>
              </div>
            </div>

            {/* Model info (no user choice — kept simple to avoid confusion) */}
            <div data-testid="model-info">
              <div className="flex items-center gap-2 font-bold text-lg">
                <Cpu size={18} className="text-[#5d8a52]" /> Model
              </div>
              <p className="text-sm text-[#6b6880] mt-1 leading-relaxed">
                Vittyyo uses leading AI — ChatGPT for writing your steps and other models behind the scenes — to give
                you the most accurate, helpful manual possible. As with any AI, mistakes can happen, so double-check
                anything important and do a little extra research when a task really matters.
              </p>
              {user?.unlimited && (
                <div className="mt-3" data-testid="settings-image-engine">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-[#6b6880] uppercase tracking-wide mb-1.5">
                    <Cpu size={13} /> Image engine <span className="text-[#b3aec6] normal-case font-semibold">(admin)</span>
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      data-testid="image-engine-gemini"
                      onClick={() => setProvider("gemini")}
                      disabled={imgBusy}
                      className={`flex-1 text-sm font-bold py-2.5 rounded-lg border transition-colors disabled:opacity-60 ${imgProvider === "gemini" ? "bg-[#4f7746] text-white border-[#4f7746]" : "bg-white text-[#4a475c] border-[#e0dcef] hover:border-[#c4b8ea]"}`}
                    >
                      Gemini
                    </button>
                    <button
                      data-testid="image-engine-openai"
                      onClick={() => setProvider("openai")}
                      disabled={imgBusy}
                      className={`flex-1 text-sm font-bold py-2.5 rounded-lg border transition-colors disabled:opacity-60 ${imgProvider === "openai" ? "bg-[#4f7746] text-white border-[#4f7746]" : "bg-white text-[#4a475c] border-[#e0dcef] hover:border-[#c4b8ea]"}`}
                    >
                      ChatGPT
                    </button>
                  </div>
                  <p className="text-[11px] text-[#9a96ac] mt-1.5 leading-snug">
                    Which model draws the photos. ChatGPT (GPT Image 1) follows prompts more literally — better accuracy, slower &amp; pricier. Gemini is faster &amp; cheaper. MovieStar likeness always uses Gemini.
                  </p>
                </div>
              )}
            </div>


            {/* Video audio mix */}
            <div data-testid="video-audio-settings">
              <div className="flex items-center gap-2 font-bold text-lg">
                <Film size={18} className="text-[#5d8a52]" /> Video Audio
              </div>
              <p className="text-sm text-[#6b6880] mt-1 mb-3 leading-relaxed">
                Set the mix for videos you create. Mute a channel to leave it out entirely (a silent video if you mute both).
              </p>
              {[
                { key: "music_gain", muteKey: "music_muted", label: "Music", def: 3 },
                { key: "voice_gain", muteKey: "voice_muted", label: "Narration", def: 8 },
                { key: "intro_gain", label: "Intro Music", def: 6, sub: "Opening swell — eases down to Music as narration starts" },
                { key: "outro_gain", label: "Outro Music", def: 6, sub: "Climbs back up over the closing clip / end card" },
              ].map(({ key, muteKey, label, def, sub }) => {
                const muted = muteKey ? !!settings[muteKey] : false;
                const val = settings[key] ?? def;
                return (
                  <div key={key} className="flex items-center justify-between gap-3 border border-[#ece9f5] rounded-2xl px-4 py-3 mb-2">
                    <div className="flex items-center gap-2.5">
                      {muteKey ? (
                        <button
                          data-testid={`mute-${key}`}
                          onClick={() => toggleMute(muteKey)}
                          aria-label={`Mute ${label}`}
                          className="w-9 h-9 rounded-xl flex items-center justify-center transition-colors shrink-0"
                          style={muted ? { background: "#fdeef0", color: "#e0455c" } : { background: "#eef3ea", color: "#5d8a52" }}
                        >
                          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                        </button>
                      ) : (
                        <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#eef3ea", color: "#5d8a52" }}>
                          <Volume2 size={18} />
                        </span>
                      )}
                      <span className="leading-tight">
                        <span className="block font-bold text-[#171426]">{label}</span>
                        {sub && <span className="block text-[11px] text-[#6b6880]">{sub}</span>}
                      </span>
                    </div>
                    <div className="flex items-center gap-2" style={{ opacity: muted ? 0.4 : 1, pointerEvents: muted ? "none" : "auto" }}>
                      <button
                        data-testid={`${key}-minus`}
                        onClick={() => setGain(key, -1)}
                        className="w-8 h-8 rounded-lg bg-[#eef3ea] text-[#5d8a52] font-extrabold flex items-center justify-center hover:bg-[#e7dffb]"
                      >
                        −
                      </button>
                      <span data-testid={`${key}-value`} className="w-7 text-center font-extrabold text-[#171426]">{muted ? "–" : val}</span>
                      <button
                        data-testid={`${key}-plus`}
                        onClick={() => setGain(key, 1)}
                        className="w-8 h-8 rounded-lg bg-[#eef3ea] text-[#5d8a52] font-extrabold flex items-center justify-center hover:bg-[#e7dffb]"
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
              {/* Auto-duck: dip the music under the host's voice automatically */}
              {(() => {
                const on = settings.auto_duck !== false;
                const lvl = settings.duck_level || "medium";
                return (
                  <div className="border border-[#ece9f5] rounded-2xl px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: on ? "#eef3ea" : "#f3f1f9", color: on ? "#5d8a52" : "#9a96ac" }}>
                          <AudioLines size={18} />
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold text-[#171426]">Auto-duck music</div>
                          <div className="text-xs text-[#9a96ac] leading-tight">{on ? "Softens the music while the host speaks, back up in the gaps" : "Music holds a steady level the whole time"}</div>
                        </div>
                      </div>
                      <button
                        data-testid="auto-duck-toggle"
                        onClick={toggleAutoDuck}
                        role="switch"
                        aria-checked={on}
                        aria-label="Auto-duck music"
                        className="relative w-12 h-7 rounded-full transition-colors shrink-0"
                        style={{ background: on ? "#5d8a52" : "#d8d5e4" }}
                      >
                        <span className="absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform" style={{ transform: on ? "translateX(20px)" : "translateX(0)" }} />
                      </button>
                    </div>
                    {on && (
                      <div className="mt-3">
                        <div className="text-xs font-semibold text-[#9a96ac] mb-1.5">How much to dip while the host speaks</div>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { key: "light", label: "Light", sub: "subtle" },
                            { key: "medium", label: "Medium", sub: "balanced" },
                            { key: "strong", label: "Strong", sub: "voice-first" },
                          ].map((o) => {
                            const active = lvl === o.key;
                            return (
                              <button
                                key={o.key}
                                data-testid={`duck-level-${o.key}`}
                                onClick={() => setDuckLevel(o.key)}
                                className="rounded-xl py-2 px-1 border-2 transition-colors text-center"
                                style={active
                                  ? { borderColor: "#5d8a52", background: "#16241a" }
                                  : { borderColor: "#2a3a26", background: "#14141b" }}
                              >
                                <div className={`text-sm font-extrabold ${active ? "text-[#3f6238]" : "text-[#171426]"}`}>{o.label}</div>
                                <div className="text-[10px] text-[#9a96ac] leading-none mt-0.5">{o.sub}</div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {user?.auth_provider === "email" && (
              <div className="pt-2 border-t border-[#f0eef7]">
                {!pwOpen ? (
                  <button
                    data-testid="change-password-btn"
                    onClick={() => setPwOpen(true)}
                    className="flex items-center gap-2 text-sm font-bold text-[#4a475c] hover:text-[#5d8a52] transition-colors"
                  >
                    <Lock size={15} className="text-[#5d8a52]" /> Change password
                  </button>
                ) : (
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2 font-bold text-sm text-[#171426]">
                      <Lock size={15} className="text-[#5d8a52]" /> Change password
                    </div>
                    <input data-testid="cp-current" type="password" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} placeholder="Current password" className="sms-input w-full px-4 py-2.5" />
                    <input data-testid="cp-new" type="password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} placeholder="New password (min 8 chars)" className="sms-input w-full px-4 py-2.5" />
                    <input data-testid="cp-confirm" type="password" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} placeholder="Confirm new password" className="sms-input w-full px-4 py-2.5" />
                    <div className="flex items-center gap-2 pt-1">
                      <button data-testid="cp-submit" onClick={changePassword} disabled={pwBusy} className="sms-solid-btn text-white font-bold px-4 py-2.5 rounded-xl disabled:opacity-70">
                        {pwBusy ? "Saving…" : "Update password"}
                      </button>
                      <button onClick={() => { setPwOpen(false); setPwCurrent(""); setPwNew(""); setPwConfirm(""); }} className="text-sm font-semibold text-[#9a96ac] hover:text-[#6b6880] px-3 py-2.5">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* MovieStar Mode */}
            {msAvailable ? (
            <div className="pt-2 border-t border-[#f0eef7]" data-testid="moviestar-section">
              <div className="flex items-center gap-2 font-bold text-lg">
                <Star size={18} className="text-[#5d8a52]" /> MovieStar Mode
                <span className="text-[10px] font-extrabold tracking-wide uppercase text-[#5d8a52] bg-[#eef3ea] px-2 py-0.5 rounded-full">Premium</span>
              </div>
              <p className="text-sm text-[#6b6880] mt-1 leading-relaxed">
                Be the star of your own how-to. Upload a photo and your likeness appears in every step of your manual —
                and the video.
              </p>

              <button
                data-testid="moviestar-how-btn"
                onClick={() => setMsHowOpen(true)}
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-[#5d8a52] hover:text-[#4f7746] transition-colors"
              >
                <Info size={14} /> How it works
              </button>

              {user?.moviestar_active ? (
                <div className="mt-3">
                  <div className="flex items-center justify-between gap-4 rounded-2xl bg-[#f6f3ff] border border-[#e7efe1] px-4 py-3">
                    <div>
                      <div className="font-bold text-[#171426] text-sm flex items-center gap-2">
                        MovieStar Mode
                        <span data-testid="moviestar-days-badge" className="inline-flex items-center gap-1 text-[11px] font-extrabold text-white px-2 py-0.5 rounded-full" style={{ background: "#4f7746" }}>
                          {user.moviestar_days_left} day{user.moviestar_days_left === 1 ? "" : "s"} remaining
                        </span>
                      </div>
                      <p className="text-xs text-[#6b6880] mt-1">
                        {settings.moviestar_enabled ? "You'll add your photo when you create your next manual." : "Turn on to star in your next manual."}
                      </p>
                    </div>
                    <button
                      data-testid="moviestar-toggle"
                      role="switch"
                      aria-checked={!!settings.moviestar_enabled}
                      onClick={onMoviestarToggle}
                      className="relative w-14 h-8 rounded-full transition-colors shrink-0"
                      style={{ background: settings.moviestar_enabled ? "#4f7746" : "#d6d3e0" }}
                    >
                      <span className="absolute top-1 w-6 h-6 bg-white rounded-full transition-all" style={{ left: settings.moviestar_enabled ? 28 : 4 }} />
                    </button>
                  </div>
                  {settings.moviestar_enabled && (
                    <div data-testid="moviestar-approved" className="mt-2 flex items-center gap-1.5 text-sm font-bold text-[#16a34a]">
                      <ShieldCheck size={15} /> Approved of Disclaimer
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-3">
                  <button
                    data-testid="moviestar-buy-btn"
                    onClick={buyMoviestar}
                    disabled={msBuyBusy}
                    className="w-full py-3 rounded-2xl font-bold text-white transition-colors disabled:opacity-70"
                    style={{ background: "linear-gradient(160deg,#7ba46f,#4f7746)" }}
                  >
                    {msBuyBusy ? "Starting checkout…" : "Unlock 30 days — $9.99"}
                  </button>
                  <p className="text-[11px] text-[#9a96ac] text-center mt-1.5">One-time payment · no subscription · no auto-renew</p>
                  <p className="text-[11px] text-[#9a96ac] text-center mt-1 leading-relaxed" data-testid="moviestar-credits-note">
                    MovieStar Mode is a separate, independent in-app purchase and does not add credits to your balance.
                    Creating manuals still uses credits as usual.
                  </p>

                  {!msBetaOpen ? (
                    <button
                      data-testid="moviestar-beta-btn"
                      onClick={() => setMsBetaOpen(true)}
                      className="mt-2 mx-auto block text-xs font-semibold text-[#9a96ac] hover:text-[#5d8a52] transition-colors"
                    >
                      Beta unlock
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        data-testid="moviestar-beta-input"
                        type="password"
                        inputMode="numeric"
                        autoFocus
                        value={msBetaCode}
                        onChange={(e) => setMsBetaCode(e.target.value)}
                        placeholder="Beta passcode"
                        className="sms-input flex-1 px-4 py-2.5 text-center tracking-widest"
                      />
                      <button
                        data-testid="moviestar-beta-submit"
                        onClick={betaUnlock}
                        disabled={msBusy}
                        className="sms-solid-btn text-white font-bold px-4 py-2.5 rounded-xl disabled:opacity-70"
                      >
                        {msBusy ? "…" : "Unlock"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            ) : (
            <div className="pt-2 border-t border-[#f0eef7]" data-testid="moviestar-section">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 font-bold text-lg text-[#9a96ac]">
                    <Star size={18} className="text-[#c9c5d6]" /> MovieStar Mode
                    <span className="text-[10px] font-bold tracking-wide uppercase text-[#9a96ac] bg-[#f1f0f5] px-2 py-0.5 rounded-full">Coming soon</span>
                  </div>
                  <Link
                    data-testid="moviestar-notify-link"
                    to="/"
                    onClick={onClose}
                    className="mt-1 inline-block text-xs font-semibold text-[#9a96ac] hover:text-[#6b6880] transition-colors"
                  >
                    Join our community to be notified
                  </Link>
                </div>
                <div
                  data-testid="moviestar-disabled-toggle"
                  aria-disabled="true"
                  className="relative w-14 h-8 rounded-full bg-[#e6e4ee] shrink-0 opacity-60 cursor-not-allowed"
                >
                  <span className="absolute top-1 left-1 w-6 h-6 bg-white rounded-full" />
                </div>
              </div>
            </div>
            )}
            <div className="pt-2 border-t border-[#f0eef7]">
            {/* Legal + discreet admin */}
            <div className="flex items-center justify-center gap-3 text-xs font-semibold text-[#9a96ac] flex-wrap">
              <button data-testid="settings-contact-link" onClick={() => setContactOpen(true)} className="hover:text-[#5d8a52] transition-colors">Contact Us</button>
              <span className="text-[#d6d3e0]">·</span>
              <Link data-testid="settings-privacy-link" to="/privacy" onClick={onClose} className="hover:text-[#5d8a52] transition-colors">Privacy Policy</Link>
              <span className="text-[#d6d3e0]">·</span>
              <Link data-testid="settings-terms-link" to="/terms" onClick={onClose} className="hover:text-[#5d8a52] transition-colors">Terms of Service</Link>
              <span className="text-[#d6d3e0]">·</span>
              <button
                data-testid="settings-admin-btn"
                onClick={() => setShowAdmin((v) => !v)}
                className="inline-flex items-center gap-1 hover:text-[#5d8a52] transition-colors"
              >
                {user?.unlimited ? <Crown size={12} className="text-[#c9c3e0]" /> : <Lock size={11} />} Admin
              </button>
            </div>

            {showAdmin && (
              <div className="mt-3 rounded-xl bg-[#f8f7fc] border border-[#eceaf4] p-3" data-testid="settings-admin-panel">
                {user?.unlimited ? (
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-1.5 text-sm font-bold text-[#5d8a52]">
                      <Crown size={14} /> Admin mode active
                    </div>
                    <Link
                      data-testid="settings-moderation-link"
                      to="/moderation"
                      onClick={onClose}
                      className="flex items-center gap-1.5 text-sm font-semibold text-[#4a475c] hover:text-[#5d8a52] transition-colors"
                    >
                      <ShieldCheck size={14} /> Moderate community
                    </Link>
                    <button
                      data-testid="settings-admin-logout"
                      onClick={doLock}
                      disabled={adminBusy}
                      className="flex items-center gap-1.5 text-sm font-semibold text-[#9a96ac] hover:text-[#e11d48] transition-colors disabled:opacity-60"
                    >
                      <LogOut size={14} /> {adminBusy ? "…" : "Log out of Admin"}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="settings-admin-input"
                      type="password"
                      autoComplete="off"
                      maxLength={40}
                      autoFocus
                      value={adminCode}
                      onChange={(e) => setAdminCode(e.target.value)}
                      placeholder="Admin passcode"
                      className="sms-input flex-1 px-4 py-2.5 text-center text-sm"
                    />
                    <button
                      data-testid="settings-admin-submit"
                      onClick={doUnlock}
                      disabled={adminBusy}
                      className="sms-solid-btn text-white font-bold px-4 py-2.5 rounded-xl disabled:opacity-70"
                    >
                      {adminBusy ? "…" : "Unlock"}
                    </button>
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
        )}

        <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-[#f0eef7]">
          <button
            data-testid="settings-done-btn"
            onClick={onClose}
            className="sms-solid-btn w-full text-white font-bold py-3.5 rounded-2xl"
          >
            {saving ? "Saving…" : "Done"}
          </button>
        </div>
      </div>
    </div>

      <MovieStarHowItWorks open={msHowOpen} onClose={() => setMsHowOpen(false)} />
      <MovieStarConsentModal open={msConsentOpen} onClose={() => setMsConsentOpen(false)} onAgree={agreeMoviestar} busy={msBusy} />
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} user={user} />
      <TopUpModal open={topUpOpen} onClose={() => setTopUpOpen(false)} />
    </>
  );
}
